import { createFileRoute, Link } from "@tanstack/react-router";
import type { SearchSchemaInput } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, lazy, Suspense } from "react";
import {
  smmListDashboard,
  smmLogDeliverable,
  smmUndoDeliverable,
  smmDecrementDeliverable,
  smmUpdateQuota,
  smmBulkUpdateQuota,
  smmNotifyAtRisk,
  smmUpdateClientPlan,
  smmListCalendarEvents,
  type SmmClientRow,
  type SmmProgressRow,
  type SmmScheduledPost,
} from "@/lib/smm.functions";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Search,
  Building2,
  Send,
  Hourglass,
  AlertTriangle,
  Minus,
  Plus,
  Undo2,
  X,
  Calendar as CalendarIcon,
  Clock,
  User as UserIcon,
  Lightbulb,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Check,
  Loader2,
  X as XIcon,
} from "lucide-react";

import { SmmAccessGate, type SmmAccess } from "@/components/crm/SmmAccessGate";
import { SMM_ROUTE_PATHS } from "@/lib/smm-routes";
const SmmScheduleDialog = lazy(() =>
  import("@/components/smm/SmmScheduleDialog").then((m) => ({ default: m.SmmScheduleDialog })),
);
const MembersPage = lazy(() =>
  import("./crm.smm.members").then((m) => ({ default: m.MembersPage })),
);
const ReportsPage = lazy(() =>
  import("./crm.smm.reports").then((m) => ({ default: m.ReportsPage })),
);
const SmmCalendarPage = lazy(() =>
  import("@/components/smm/SmmCalendarPage").then((m) => ({ default: m.SmmCalendarPage })),
);
const CalendarFallback = () => (
  <div className="flex items-center justify-center py-24 text-sm text-slate-400">Loading…</div>
);

export const Route = createFileRoute("/crm/smm/")({
  validateSearch: (s: Record<string, unknown> & SearchSchemaInput) => ({
    view:
      s.view === "members" || s.view === "reports" || s.view === "operations" || s.view === "calendar"
        ? (s.view as "members" | "reports" | "operations" | "calendar")
        : undefined,
    routeRecovery: s.routeRecovery === "1" || s.routeRecovery === 1 || s.routeRecovery === true ? 1 : undefined,
  }),
  component: SmmRouteComponent,
});


const STATUS_STYLES: Record<
  string,
  { dot: string; label: string; chipBg: string; chipFg: string; rowBg: string }
> = {
  overdue:     { dot: "#ef4444", label: "Overdue",     chipBg: "transparent", chipFg: "#ef4444", rowBg: "#fff1f0" },
  at_risk:     { dot: "#ef4444", label: "At Risk",     chipBg: "transparent", chipFg: "#ef4444", rowBg: "#fff5f2" },
  in_progress: { dot: "#f59e0b", label: "In Progress", chipBg: "transparent", chipFg: "#b45309", rowBg: "#fffaf0" },
  completed:   { dot: "#22c55e", label: "Completed",   chipBg: "transparent", chipFg: "#16a34a", rowBg: "#f1faf3" },
  inactive:    { dot: "#94a3b8", label: "Inactive",    chipBg: "transparent", chipFg: "#64748b", rowBg: "#ffffff" },
};

const HEADING_FONT = "'Space Grotesk', system-ui, sans-serif";
const BODY_FONT = "'DM Sans', system-ui, sans-serif";

function fmtEUR(n: number | null) {
  if (n == null) return "—";
  return `€${Math.round(n).toLocaleString("en-US")}`;
}
function endOfMonth(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function fmtLongDate(d: Date) {
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

const LOGO_PALETTE = [
  { bg: "#1f1f1f", fg: "#f5f5f5" },
  { bg: "#fef3c7", fg: "#92400e" },
  { bg: "#dcfce7", fg: "#15803d" },
  { bg: "#dbeafe", fg: "#1d4ed8" },
  { bg: "#fce7f3", fg: "#9d174d" },
  { bg: "#ede9fe", fg: "#5b21b6" },
];
function logoFor(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return LOGO_PALETTE[h % LOGO_PALETTE.length];
}

function SmmRouteComponent() {
  const search = Route.useSearch();
  return (
    <SmmAccessGate require={search.view === "members" ? "manage" : "member"}>
      {(access) => <SmmPageEntry access={access} />}
    </SmmAccessGate>
  );
}

function SmmPageEntry({ access }: { access: SmmAccess }) {
  const search = Route.useSearch();

  if (search.view === "members") {
    return <Suspense fallback={<CalendarFallback />}><MembersPage /></Suspense>;
  }

  if (access.canManage) {
    if (search.view === "calendar") {
      return <Suspense fallback={<CalendarFallback />}><SmmCalendarPage /></Suspense>;
    }
    if (search.view === "reports") {
      return (
        <Suspense fallback={<CalendarFallback />}>
          <ReportsPage access={{ isSmmAdmin: access.isSmmAdmin, userId: access.userId }} />
        </Suspense>
      );
    }
    return <SmmPage />;
  }

  // Workers → Operations workspace (with quick calendar switch)
  if (search.view === "calendar") return <Suspense fallback={<CalendarFallback />}><SmmCalendarPage /></Suspense>;
  return <SmmPage />;
}







function SmmPage() {
  const listFn = useServerFn(smmListDashboard);
  const logFn = useServerFn(smmLogDeliverable);
  const undoFn = useServerFn(smmUndoDeliverable);
  const decrementFn = useServerFn(smmDecrementDeliverable);
  const updateQuotaFn = useServerFn(smmUpdateQuota);
  const qc = useQueryClient();

  const bulkFn = useServerFn(smmBulkUpdateQuota);
  const notifyFn = useServerFn(smmNotifyAtRisk);
  const updatePlanFn = useServerFn(smmUpdateClientPlan);

  const [search, setSearch] = useState("");
  const [mineOnly, setMineOnly] = useState(false);
  const [toast, setToast] = useState<{ token?: string; label: string } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editRow, setEditRow] = useState<SmmClientRow | null>(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [quotaPrompt, setQuotaPrompt] = useState<{
    row: SmmClientRow;
    service_key: string;
    label: string;
    current: number;
  } | null>(null);
  const [quotaValue, setQuotaValue] = useState("");
  const [quotaSaving, setQuotaSaving] = useState(false);
  const [expandedKey, setExpandedKey] = useState<Record<string, string | null>>({});
  const [dlFilter, setDlFilter] = useState<"overdue" | "week" | "completed">("week");
  const [dlSort, setDlSort] = useState<"urgency" | "date" | "remaining">("urgency");
  const [markingKey, setMarkingKey] = useState<string | null>(null);
  const [calendarOpen, setCalendarOpen] = useState<null | "all" | string>(null);
  const [scheduleRow, setScheduleRow] = useState<SmmClientRow | null>(null);


  const q = useQuery({
    queryKey: ["smm", "dashboard", { search, mineOnly }],
    queryFn: () => listFn({ data: { search, mineOnly } }),
    staleTime: 30_000,
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    const channel = supabase
      .channel("smm-deliverables")
      .on("postgres_changes", { event: "*", schema: "public", table: "smm_deliverables" }, () => {
        qc.invalidateQueries({ queryKey: ["smm", "dashboard"] });
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [qc]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const key = "smm-notify-run";
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    notifyFn({ data: { silent: true } }).catch(() => {});
  }, [notifyFn]);

  const logMutation = useMutation({
    mutationFn: (vars: { subscription_id: string; service_key: string; label: string }) =>
      logFn({ data: { subscription_id: vars.subscription_id, service_key: vars.service_key } }),
    onSuccess: (res, vars) => {
      setToast({ token: res.undo_token, label: `${vars.label} logged` });
      qc.invalidateQueries({ queryKey: ["smm", "dashboard"] });
    },
  });

  const undoMutation = useMutation({
    mutationFn: (token: string) => undoFn({ data: { undo_token: token } }),
    onSuccess: () => {
      setToast(null);
      qc.invalidateQueries({ queryKey: ["smm", "dashboard"] });
    },
  });

  const decrementMutation = useMutation({
    mutationFn: (vars: { subscription_id: string; service_key: string }) =>
      decrementFn({ data: vars }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["smm", "dashboard"] }),
  });

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  const allRows = q.data?.rows ?? [];
  const kpis = q.data?.kpis ?? { active: 0, delivered: 0, pending: 0, overdue: 0 };

  const rows = useMemo(() => {
    if (!dateFrom && !dateTo) return allRows;
    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(dateTo) : null;
    if (to) to.setHours(23, 59, 59, 999);
    const eom = endOfMonth();
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return allRows.filter((row) =>
      row.items.some((it) => {
        if (it.status === "completed" || it.status === "inactive") return false;
        const due = it.status === "overdue" ? today : eom;
        if (from && due < from) return false;
        if (to && due > to) return false;
        return true;
      }),
    );
  }, [allRows, dateFrom, dateTo]);

  const statusCounts = useMemo(() => {
    const c = { overdue: 0, at_risk: 0, in_progress: 0, completed: 0, inactive: 0 };
    for (const r of rows) {
      const s = (r.worst_status as keyof typeof c) ?? "in_progress";
      if (s in c) c[s] += 1;
    }
    return c;
  }, [rows]);

  const monthLabel = useMemo(
    () => new Date().toLocaleString("en-US", { month: "long", year: "numeric" }),
    [],
  );

  type DeadlineEntry = { row: SmmClientRow; item: SmmProgressRow; due: Date; daysOffset: number; completed: boolean };
  const allDeadlines = useMemo<DeadlineEntry[]>(() => {
    const out: DeadlineEntry[] = [];
    const eom = endOfMonth();
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const eomOffset = Math.round((eom.getTime() - today.getTime()) / 86400000);
    for (const row of rows) {
      for (const it of row.items) {
        if (it.status === "inactive") continue;
        const isOverdue = it.status === "overdue";
        const isCompleted = it.status === "completed" || (it.monthly_quota > 0 && it.remaining <= 0);
        const due = isOverdue ? today : eom;
        const daysOffset = isOverdue ? -1 : eomOffset;
        out.push({ row, item: it, due, daysOffset, completed: isCompleted });
      }
    }
    return out;
  }, [rows]);

  // Real scheduled posts from smm_deliverables (what the user actually plans on the calendar)
  const listCalFn = useServerFn(smmListCalendarEvents);
  const scheduledQ = useQuery({
    queryKey: ["smm", "calendar", "next7"],
    queryFn: () => listCalFn({ data: {} }),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });
  useEffect(() => {
    const ch = supabase
      .channel("smm-deliverables-sidebar")
      .on("postgres_changes", { event: "*", schema: "public", table: "smm_deliverables" }, () => {
        qc.invalidateQueries({ queryKey: ["smm", "calendar"] });
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [qc]);

  type CalendarEntry =
    | { kind: "scheduled"; post: SmmScheduledPost; clientName: string; label: string; due: Date; daysOffset: number; isOverdue: boolean }
    | { kind: "deadline"; row: SmmClientRow; item: SmmProgressRow; due: Date; daysOffset: number; isOverdue: boolean };

  const calendar = useMemo<CalendarEntry[]>(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const in7 = new Date(today); in7.setDate(in7.getDate() + 7);

    const subMap = new Map<string, SmmClientRow>();
    const labelMap = new Map<string, string>();
    for (const r of rows) {
      subMap.set(r.subscription_id, r);
      for (const it of r.items) labelMap.set(`${r.subscription_id}:${it.service_key}`, it.label);
    }

    const scheduledEntries: CalendarEntry[] = [];
    for (const p of scheduledQ.data ?? []) {
      const whenStr = p.scheduled_for ?? p.delivered_at;
      if (!whenStr) continue;
      const due = new Date(whenStr);
      if (isNaN(due.getTime())) continue;
      const isPublished = p.post_status === "published";
      // include upcoming (<=7d) and overdue-scheduled (past + still scheduled)
      const inWindow = due <= in7;
      if (!inWindow) continue;
      if (isPublished && due < today) continue;
      const row = subMap.get(p.subscription_id);
      if (!row) continue; // don't show items outside current filter
      const clientName = row.client_name;
      const label = labelMap.get(`${p.subscription_id}:${p.service_key}`) ?? p.service_key;
      const daysOffset = Math.round((due.getTime() - today.getTime()) / 86400000);
      scheduledEntries.push({
        kind: "scheduled", post: p, clientName, label, due,
        daysOffset, isOverdue: !isPublished && due < today,
      });
    }
    scheduledEntries.sort((a, b) => a.due.getTime() - b.due.getTime());

    if (scheduledEntries.length > 0) return scheduledEntries.slice(0, 8);

    // Fallback to derived monthly deadlines when nothing is actually scheduled
    return allDeadlines
      .filter((d) => !d.completed && (d.daysOffset < 0 || d.daysOffset <= 7))
      .sort((a, b) => a.daysOffset - b.daysOffset)
      .slice(0, 5)
      .map((d) => ({
        kind: "deadline" as const,
        row: d.row, item: d.item, due: d.due, daysOffset: d.daysOffset,
        isOverdue: d.item.status === "overdue",
      }));
  }, [allDeadlines, rows, scheduledQ.data]);




  return (
    <div className="space-y-4 text-[#212529]">
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-2.5">
          <KpiTile icon={Building2} tint="#0d6efd" label="Active clients" value={kpis.active} sub="this month" />
          <KpiTile icon={Send} tint="#198754" label="Delivered" value={kpis.delivered} sub="published" />
          <KpiTile icon={Hourglass} tint="#fd7e14" label="Pending" value={kpis.pending} sub="to publish" />
          <KpiTile icon={AlertTriangle} tint="#dc3545" label="Overdue" value={kpis.overdue} sub="past due" />
        </div>

        {/* Quick Filter & Controls Bar */}
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-white px-3 py-2.5" style={{ borderColor: "#eceff3" }}>
          <div className="flex flex-wrap items-center gap-2">
            {[
              { k: "overdue",     label: "Overdue",     border: "border-red-100",     bg: "bg-red-50/50 hover:bg-red-50",         fg: "text-red-600",     badge: "bg-red-600" },
              { k: "at_risk",     label: "At Risk",     border: "border-orange-100",  bg: "bg-orange-50/50 hover:bg-orange-50",   fg: "text-orange-600",  badge: "bg-orange-600" },
              { k: "in_progress", label: "In Progress", border: "border-yellow-100",  bg: "bg-yellow-50/50 hover:bg-yellow-50",   fg: "text-yellow-700",  badge: "bg-yellow-600" },
              { k: "completed",   label: "Completed",   border: "border-emerald-100", bg: "bg-emerald-50/50 hover:bg-emerald-50", fg: "text-emerald-600", badge: "bg-emerald-600" },
              { k: "inactive",    label: "Inactive",    border: "border-[#e8ecf1]",   bg: "bg-[#f8fafc] hover:bg-[#e8ecf1]",      fg: "text-[#64748b]",   badge: "bg-[#94a3b8]" },
            ].map((s) => (
              <button
                key={s.k}
                type="button"
                className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${s.border} ${s.bg} ${s.fg}`}
              >
                {s.label}
                <span className={`flex h-5 min-w-[20px] items-center justify-center rounded px-1 text-[10px] text-white ${s.badge}`}>
                  {statusCounts[s.k as keyof typeof statusCounts]}
                </span>
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-[#94a3b8]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by company or contact..."
                aria-label="Search clients by company or contact name"
                className="pl-8 pr-3 h-9 text-xs w-64 rounded-lg border border-[#e8ecf1] bg-[#fafbfc] text-[#0f172a] placeholder:text-[#94a3b8] focus:border-[#3b82f6] focus:outline-none focus:ring-1 focus:ring-[#3b82f6]"
              />
            </div>
            <button
              type="button"
              onClick={() => setMineOnly((v) => !v)}
              aria-pressed={mineOnly}
              className={`inline-flex items-center gap-2 px-3 h-9 rounded-lg border text-xs font-semibold transition-colors ${
                mineOnly
                  ? "bg-[#3b82f6] border-[#3b82f6] text-white"
                  : "bg-white border-[#e8ecf1] text-[#3b82f6] hover:bg-[#e8ecf1]/50"
              }`}
            >
              <UserIcon className="size-3.5" /> My Work
            </button>
            <span className="text-[11px] font-bold text-[#94a3b8] uppercase tracking-wider">Due between</span>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                aria-label="Due from"
                className="w-36 rounded-lg border border-[#e8ecf1] bg-[#fafbfc] px-2.5 py-1.5 text-xs text-[#0f172a] focus:border-[#3b82f6] focus:outline-none focus:ring-1 focus:ring-[#3b82f6]"
              />
              <span className="text-[#94a3b8]">—</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                aria-label="Due to"
                className="w-36 rounded-lg border border-[#e8ecf1] bg-[#fafbfc] px-2.5 py-1.5 text-xs text-[#0f172a] focus:border-[#3b82f6] focus:outline-none focus:ring-1 focus:ring-[#3b82f6]"
              />
              {(dateFrom || dateTo) && (
                <button
                  onClick={() => { setDateFrom(""); setDateTo(""); }}
                  className="h-8 px-2 rounded-md hover:bg-[#f3f4f6] text-[11px] text-[#64748b]"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Body: clients grid | right rail */}
        <div className="grid grid-cols-1 2xl:grid-cols-[minmax(0,1fr)_340px] gap-6">
          <div className="space-y-6 min-w-0">
            {/* Table card */}
            <div className="bg-white rounded-xl border border-[#e8ecf1] shadow-sm overflow-hidden">



              {selected.size > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 md:px-6 py-3 border-b border-[#eceef5] bg-[#eef0ff]">
                  <div className="text-sm text-[#1a1a2e]">
                    <span className="font-bold">{selected.size}</span> client
                    {selected.size === 1 ? "" : "s"} selected
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setBulkOpen(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#0d6efd] hover:bg-[#0b5ed7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-2"
                    >
                      Set / Adjust Quotas
                    </button>
                    <button
                      onClick={() => setSelected(new Set())}
                      className="text-xs text-[#6b7280] hover:text-[#0a0a14] px-2 py-1 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-2"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}

              {q.isLoading ? (
                <div className="p-4 md:p-6">
                  <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <li
                        key={i}
                        className="bg-white border border-[#eceef5] rounded-xl p-4 shadow-sm animate-pulse"
                        aria-hidden="true"
                      >
                        <div className="flex items-start gap-3 mb-3">
                          <div className="size-9 rounded-lg bg-zinc-200 shrink-0" />
                          <div className="flex-1 min-w-0 space-y-2">
                            <div className="h-3.5 w-2/3 rounded bg-zinc-200" />
                            <div className="h-2.5 w-1/2 rounded bg-zinc-100" />
                          </div>
                        </div>
                        <div className="h-2.5 w-3/4 rounded bg-zinc-100 mb-3" />
                        <div className="grid grid-cols-3 gap-2">
                          {[0, 1, 2].map((j) => (
                            <div key={j} className="h-14 rounded-lg bg-zinc-100" />
                          ))}
                        </div>
                      </li>
                    ))}
                  </ul>
                  <span className="sr-only" role="status" aria-live="polite">
                    Loading clients
                  </span>
                </div>
              ) : q.isError ? (
                <div className="p-12 text-center">
                  <div className="inline-flex size-12 items-center justify-center rounded-full bg-red-50 text-red-600 mb-3">
                    <AlertTriangle className="size-6" />
                  </div>
                  <div className="text-sm font-semibold text-[#0a0a14]">Couldn't load delivery data</div>
                  <div className="text-xs text-[#6b7280] mt-1 max-w-md mx-auto">
                    {(q.error as Error)?.message ?? "Unknown error reaching smm_subscriptions."}
                  </div>
                  <button
                    onClick={() => q.refetch()}
                    className="mt-4 inline-flex items-center gap-1.5 px-4 h-9 rounded-lg bg-[#0a0a14] text-white text-xs font-semibold hover:bg-[#1f1f2e]"
                  >
                    <Loader2 className={`size-3.5 ${q.isFetching ? "animate-spin" : ""}`} /> Retry
                  </button>
                </div>
              ) : allRows.length === 0 ? (
                <div className="p-12 text-center text-[#6b7280] text-sm">
                  <div className="text-sm font-semibold text-[#0a0a14] mb-1">No active subscriptions yet</div>
                  <p className="max-w-md mx-auto">
                    Close a deal as "won" in the CRM to spin one up automatically. Tables checked:{" "}
                    <code className="text-[11px]">smm_subscriptions</code>,{" "}
                    <code className="text-[11px]">smm_quota_items</code>,{" "}
                    <code className="text-[11px]">smm_deliverables</code>.
                  </p>
                  <button
                    onClick={() => q.refetch()}
                    className="mt-4 inline-flex items-center gap-1.5 px-4 h-9 rounded-lg border border-[#e5e7eb] bg-white text-[#0a0a14] text-xs font-semibold hover:bg-[#f3f4f6]"
                  >
                    <Loader2 className={`size-3.5 ${q.isFetching ? "animate-spin" : ""}`} /> Retry
                  </button>
                </div>
              ) : rows.length === 0 ? (
                <div className="p-12 text-center text-[#6b7280] text-sm">
                  No clients match the current date range filter.
                  <div className="mt-3">
                    <button
                      onClick={() => { setDateFrom(""); setDateTo(""); }}
                      className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg border border-[#e5e7eb] bg-white text-[#0a0a14] text-xs font-semibold hover:bg-[#f3f4f6]"
                    >
                      Clear date filter
                    </button>
                  </div>
                </div>

              ) : (
                <div className="p-4 md:p-6">
                  <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {rows.map((row) => {
                      const styles = STATUS_STYLES[row.worst_status] ?? STATUS_STYLES.in_progress;
                      const eom = endOfMonth();
                      const daysLeft = row.items[0]?.days_left ?? 0;
                      const isOverdue = row.worst_status === "overdue";
                      const subText = isOverdue
                        ? `${Math.max(1, Math.abs(daysLeft))}d overdue`
                        : daysLeft === 0
                          ? "Due today"
                          : daysLeft === 1
                            ? "in 1 day"
                            : `in ${daysLeft}d`;
                      const logo = logoFor(row.client_name);
                      const handle = `@${row.client_name.toLowerCase().replace(/[^a-z0-9]/g, "")}`;
                      const isSelected = selected.has(row.subscription_id);
                      const accentBorder = isOverdue
                        ? "hover:border-red-200"
                        : row.worst_status === "at_risk"
                          ? "hover:border-red-200"
                          : row.worst_status === "in_progress"
                            ? "hover:border-amber-200"
                            : row.worst_status === "completed"
                              ? "hover:border-emerald-200"
                              : "hover:border-indigo-200";
                      const totalQuota = row.items.reduce((s, i) => s + (i.monthly_quota || 0), 0);
                      const totalDone = row.items.reduce((s, i) => s + (i.delivered || 0), 0);
                      const pct = totalQuota > 0 ? Math.min(100, Math.round((totalDone / totalQuota) * 100)) : 0;
                      const progressColor = isOverdue
                        ? "#ef4444"
                        : row.worst_status === "completed"
                          ? "#10b981"
                          : row.worst_status === "in_progress"
                            ? "#f59e0b"
                            : "#6366f1";
                      return (
                        <li
                          id={`client-${row.subscription_id}`}
                          key={row.subscription_id}

                          tabIndex={0}
                          role="group"
                          aria-label={`${row.client_name}, ${styles.label}. Press Space to ${isSelected ? "deselect" : "select"}, Enter to edit.`}
                          aria-selected={isSelected}
                          onKeyDown={(e) => {
                            const tag = (e.target as HTMLElement).tagName;
                            if (tag === "INPUT" || tag === "BUTTON" || tag === "A" || tag === "TEXTAREA") return;
                            if (e.key === " ") {
                              e.preventDefault();
                              setSelected((prev) => {
                                const next = new Set(prev);
                                if (next.has(row.subscription_id)) next.delete(row.subscription_id);
                                else next.add(row.subscription_id);
                                return next;
                              });
                            } else if (e.key === "Enter") {
                              e.preventDefault();
                              setEditRow(row);
                            }
                          }}
                          className={`group relative bg-white border rounded-xl p-3.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-2 ${accentBorder} ${isSelected ? "border-[#0d6efd] ring-2 ring-[#0d6efd]/15" : "border-[#eceff3]"}`}
                          style={{ fontFamily: "'DM Sans', sans-serif" }}
                        >
                          {/* Header */}
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex gap-3 min-w-0">
                              <div className="relative shrink-0">
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={(c) => {
                                    setSelected((prev) => {
                                      const next = new Set(prev);
                                      if (c) next.add(row.subscription_id);
                                      else next.delete(row.subscription_id);
                                      return next;
                                    });
                                  }}
                                  aria-label={`Select ${row.client_name}`}
                                  className="absolute -top-1.5 -left-1.5 size-4 z-10 bg-white border border-slate-300 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-2"
                                />
                                <div
                                  className="size-12 rounded-xl grid place-items-center text-base font-bold"
                                  style={{ background: logo.bg, color: logo.fg, fontFamily: "'Space Grotesk', sans-serif" }}
                                  aria-hidden="true"
                                >
                                  {initials(row.client_name)}
                                </div>
                              </div>
                              <div className="min-w-0">
                                <h3 className="text-[15px] font-bold text-slate-900 leading-tight break-words" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                                  {row.company_name?.trim() || row.client_name || "Unnamed Client"}
                                </h3>
                                {row.company_name?.trim() ? (
                                  row.company_name.trim() !== row.client_name ? (
                                    <p className="text-[11px] text-slate-500 mt-0.5 truncate font-medium">{row.client_name}</p>
                                  ) : null
                                ) : (
                                  <p className="text-[11px] text-amber-600 mt-0.5 truncate font-medium" title="No company name on file — using contact name">
                                    Individual · no company set
                                  </p>
                                )}
                                <p className="text-[11px] text-slate-400 mt-0.5 truncate">{handle}</p>
                                <span
                                  className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider whitespace-nowrap"
                                  style={{ color: styles.chipFg, background: `${styles.dot}1a` }}
                                >
                                  <span className="size-1.5 rounded-full" style={{ background: styles.dot }} />
                                  {styles.label}
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={() => setEditRow(row)}
                              className="-m-1 p-2 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-2"
                              aria-label={`Edit ${row.client_name}`}
                              title="Edit plan & quotas"
                            >
                              <MoreHorizontal className="size-5" />
                            </button>
                          </div>

                          {/* Package Details */}
                          <div className="flex items-end justify-between gap-2 mb-2">
                            <div className="text-[11px] text-slate-500 font-medium truncate min-w-0">
                              {row.package_type} <span className="text-slate-300 mx-1">•</span> {fmtEUR(row.monthly_price)}/mo
                            </div>
                            <div
                              className="text-[11px] font-bold shrink-0 flex items-center gap-1"
                              style={{ color: isOverdue ? "#ef4444" : row.worst_status === "in_progress" ? "#d97706" : "#16a34a" }}
                            >
                              {subText}
                              {isOverdue && <AlertTriangle className="size-3" />}
                            </div>
                          </div>

                          {/* Progress */}
                          <div className="w-full bg-slate-100 h-1.5 rounded-full mb-3 overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{ width: `${pct}%`, background: progressColor }}
                            />
                          </div>

                          {/* Metrics grid */}
                          <div className="grid grid-cols-3 gap-2 mb-3">
                            {(["stories", "feed_posts", "reels"] as const).map((sk) => {
                              const item = row.items.find((i) => i.service_key === sk);
                              const label = sk === "feed_posts" ? "Feed" : sk[0].toUpperCase() + sk.slice(1);
                              const skLabel = sk === "feed_posts" ? "Feed Posts" : label;
                              if (!item || item.monthly_quota === 0) {
                                return (
                                  <button
                                    key={sk}
                                    onClick={() => {
                                      setQuotaPrompt({ row, service_key: sk, label: skLabel, current: 0 });
                                      setQuotaValue("0");
                                    }}
                                    className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 py-2.5 px-1 text-center hover:border-indigo-400 transition-colors opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-2"
                                    aria-label={`Add ${skLabel} quota for ${row.client_name}`}
                                  >
                                    <span className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">{label}</span>
                                    <span className="block text-xl font-bold text-slate-300" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>—</span>
                                    <span className="block text-[10px] text-slate-400 font-medium mt-1">N/A</span>
                                  </button>
                                );
                              }
                              const active = expandedKey[row.subscription_id] === sk;
                              const isDone = item.status === "completed";
                              return (
                                <div
                                  key={sk}
                                  className={`rounded-2xl py-2 px-1 text-center border transition-colors ${
                                    active
                                      ? "bg-white border-slate-800 shadow-sm"
                                      : isDone
                                        ? "bg-emerald-50/50 border-emerald-100"
                                        : "bg-slate-50/80 border-slate-100"
                                  }`}
                                >
                                  <div className={`text-[10px] font-bold uppercase tracking-widest mb-1.5 ${isDone ? "text-emerald-700" : active ? "text-emerald-600" : "text-slate-400"}`}>
                                    {label}
                                  </div>
                                  <CellCounter
                                    done={item.delivered}
                                    quota={item.monthly_quota}
                                    status={item.status}
                                    label={skLabel}
                                    active={active}
                                    onActivate={() => setExpandedKey((p) => ({ ...p, [row.subscription_id]: sk }))}
                                    onCollapse={() => setExpandedKey((p) => ({ ...p, [row.subscription_id]: null }))}
                                    onCommitPlus={async () => {
                                      await logMutation.mutateAsync({
                                        subscription_id: row.subscription_id,
                                        service_key: sk,
                                        label: `${item.label} for ${row.client_name}`,
                                      });
                                    }}
                                    onCommitMinus={async () => {
                                      await decrementMutation.mutateAsync({
                                        subscription_id: row.subscription_id,
                                        service_key: sk,
                                      });
                                    }}
                                  />
                                </div>
                              );
                            })}
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => setEditRow(row)}
                              className="flex-1 h-8 px-3 bg-slate-900 text-white text-[11px] font-bold rounded-lg hover:bg-slate-800 transition-colors shadow-sm shadow-slate-200 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-2"
                              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                            >
                              Review
                            </button>
                            <button
                              type="button"
                              onClick={() => setScheduleRow(row)}
                              className="h-8 px-2.5 border border-slate-200 text-slate-700 text-[11px] font-semibold rounded-lg hover:bg-slate-50 transition-colors inline-flex items-center gap-1 whitespace-nowrap shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-2"
                              aria-label={`Add calendar entry for ${row.client_name}`}
                              title="Schedule posts"
                            >
                              <CalendarIcon className="size-3.5" />
                              <span className="hidden lg:inline">Schedule</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setCalendarOpen(row.subscription_id)}
                              className="grid size-8 shrink-0 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors place-items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-2"
                              aria-label={`View deadlines for ${row.client_name}`}
                              title="View deadlines"
                            >
                              <Clock className="size-3.5" />
                            </button>
                            <a
                              href={`/crm/${row.client_id}`}
                              className="grid size-8 shrink-0 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors place-items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-2"
                              aria-label={`Open ${row.client_name} client page`}
                            >
                              <ChevronRight className="size-3.5" />
                            </a>
                          </div>

                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-t text-xs text-[#6c757d]" style={{ borderColor: "#eceff3" }}>
                Showing {rows.length} of {kpis.active} clients
              </div>
            </div>
          </div>

          {/* Right rail */}
          <aside className="space-y-5">
            {/* Deadlines */}
            {(() => {
              const overdueCount = allDeadlines.filter((d) => !d.completed && d.daysOffset < 0).length;
              const weekCount = allDeadlines.filter((d) => !d.completed && d.daysOffset >= 0 && d.daysOffset <= 7).length;
              const completedCount = allDeadlines.filter((d) => d.completed).length;
              const filtered = allDeadlines.filter((d) => {
                if (dlFilter === "overdue") return !d.completed && d.daysOffset < 0;
                if (dlFilter === "week") return !d.completed && d.daysOffset >= 0 && d.daysOffset <= 7;
                return d.completed;
              });
              const sorted = [...filtered].sort((a, b) => {
                if (dlSort === "date") return a.due.getTime() - b.due.getTime();
                if (dlSort === "remaining") return b.item.remaining - a.item.remaining;
                // urgency: overdue first, then by daysOffset, then by remaining desc
                if (a.daysOffset !== b.daysOffset) return a.daysOffset - b.daysOffset;
                return b.item.remaining - a.item.remaining;
              });
              const visible = sorted.slice(0, 8);
              const filterTabs = [
                { key: "overdue" as const, label: "Overdue", count: overdueCount, color: "#dc2626" },
                { key: "week" as const, label: "Next 7d", count: weekCount, color: "#b45309" },
                { key: "completed" as const, label: "Completed", count: completedCount, color: "#16a34a" },
              ];
              return (
                <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: "#eceff3" }}>
                  <div className="px-5 pt-5 pb-3 border-b border-[#f1f2f7]">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="min-w-0">
                        <h3 className="text-xs font-bold uppercase tracking-wider text-[#0a0a14]" style={{ fontFamily: HEADING_FONT }}>
                          This Week Deadlines
                        </h3>
                        <p className="text-[11px] text-[#6b7280] mt-0.5">
                          {overdueCount} overdue · {weekCount} due ≤ 7d · {completedCount} done
                        </p>
                      </div>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#f5f6fb] px-2.5 py-1 text-[10px] font-semibold text-[#0d6efd]">
                        <span className="size-1.5 rounded-full bg-[#0d6efd] animate-pulse" />
                        Live
                      </span>
                    </div>
                    <div className="flex items-center gap-1 mb-2" role="tablist" aria-label="Deadline filter">
                      {filterTabs.map((t) => {
                        const active = dlFilter === t.key;
                        return (
                          <button
                            key={t.key}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => setDlFilter(t.key)}
                            className={`flex-1 px-2 py-1.5 rounded-md text-[11px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-1 ${active ? "bg-[#0a0a14] text-white" : "bg-[#f5f6fb] text-[#6b7280] hover:bg-[#eceef5]"}`}
                          >
                            {t.label}
                            <span className={`ml-1 tabular-nums ${active ? "text-white/80" : ""}`} style={{ color: active ? undefined : t.color }}>
                              {t.count}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <label htmlFor="dl-sort" className="text-[10px] font-semibold uppercase tracking-wider text-[#9ca3af]">Sort</label>
                      <select
                        id="dl-sort"
                        value={dlSort}
                        onChange={(e) => setDlSort(e.target.value as typeof dlSort)}
                        className="text-[11px] font-medium text-[#0a0a14] bg-[#f5f6fb] border border-transparent rounded-md px-2 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd]"
                      >
                        <option value="urgency">Urgency</option>
                        <option value="date">Date</option>
                        <option value="remaining">Remaining quota</option>
                      </select>
                    </div>
                  </div>

                  {q.isLoading ? (
                    <ul className="divide-y divide-[#f1f2f7]" aria-hidden="true">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <li key={i} className="px-5 py-3 flex items-center gap-3 animate-pulse">
                          <div className="w-11 h-11 rounded-lg bg-zinc-200 shrink-0" />
                          <div className="flex-1 space-y-2">
                            <div className="h-3 w-2/3 rounded bg-zinc-200" />
                            <div className="h-2 w-full rounded bg-zinc-100" />
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : visible.length === 0 ? (
                    <div className="px-5 py-8 text-center">
                      <div className="mx-auto size-10 grid place-items-center rounded-full bg-[#dcfce7] text-[#16a34a] mb-2">
                        <Check className="size-5" />
                      </div>
                      <p className="text-sm font-semibold text-[#0a0a14]">
                        {dlFilter === "overdue" ? "No overdue posts" : dlFilter === "completed" ? "Nothing completed yet" : "No deadlines this week"}
                      </p>
                      <p className="text-[11px] text-[#6b7280] mt-0.5">
                        {dlFilter === "completed" ? "Log a delivery from a client card." : "All quotas on track."}
                      </p>
                    </div>
                  ) : (
                    <ul className="divide-y divide-[#f1f2f7]">
                      {visible.map(({ row, item, due, daysOffset, completed }, i) => {
                        const rowKey = `${row.subscription_id}-${item.service_key}`;
                        const isOverdue = !completed && daysOffset < 0;
                        const urgent = !completed && daysOffset >= 0 && daysOffset <= 2;
                        const tagBg = completed ? "#dcfce7" : isOverdue ? "#fee2e2" : urgent ? "#fef3c7" : "#dcfce7";
                        const tagFg = completed ? "#16a34a" : isOverdue ? "#dc2626" : urgent ? "#b45309" : "#16a34a";
                        const dateBg = completed ? "#10b981" : isOverdue ? "#ef4444" : urgent ? "#f59e0b" : "#10b981";
                        const tagText = completed
                          ? "Done"
                          : isOverdue
                            ? `${Math.abs(daysOffset)}d overdue`
                            : daysOffset === 0
                              ? "Today"
                              : `${daysOffset}d left`;
                        const pct = item.monthly_quota > 0 ? Math.round(((item.monthly_quota - item.remaining) / item.monthly_quota) * 100) : 0;
                        const jump = () => {
                          const el = document.getElementById(`client-${row.subscription_id}`);
                          if (el) {
                            el.scrollIntoView({ behavior: "smooth", block: "center" });
                            el.classList.add("ring-2", "ring-[#0d6efd]");
                            setTimeout(() => el.classList.remove("ring-2", "ring-[#0d6efd]"), 1600);
                          }
                        };
                        const marking = markingKey === rowKey && logMutation.isPending;
                        return (
                          <li key={`${rowKey}-${i}`} className="px-5 py-3 flex items-center gap-3 hover:bg-[#fafbfc] transition-colors">
                            <div className="w-11 shrink-0 text-center rounded-lg overflow-hidden border border-[#eceef5]">
                              <div className="text-[9px] font-bold uppercase text-white py-0.5 tracking-wider" style={{ background: dateBg }}>
                                {due.toLocaleString("en-US", { month: "short" })}
                              </div>
                              <div className="text-base font-bold text-[#0a0a14] py-0.5" style={{ fontFamily: HEADING_FONT }}>
                                {due.getDate()}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <button
                                  type="button"
                                  onClick={jump}
                                  title={`Jump to ${row.client_name}`}
                                  className="text-sm font-semibold text-[#0a0a14] truncate hover:text-[#0d6efd] hover:underline focus-visible:outline-none focus-visible:text-[#0d6efd] text-left"
                                >
                                  {row.client_name}
                                </button>
                                <span
                                  className="shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap"
                                  style={{ background: tagBg, color: tagFg }}
                                >
                                  {tagText}
                                </span>
                              </div>
                              <div className="mt-1 flex items-center gap-2">
                                <div className="flex-1 h-1.5 rounded-full bg-[#f1f2f7] overflow-hidden">
                                  <div className="h-full rounded-full" style={{ width: `${pct}%`, background: dateBg }} />
                                </div>
                                <span className="text-[10px] font-medium text-[#6b7280] tabular-nums shrink-0">
                                  {item.remaining}/{item.monthly_quota} {item.label}
                                </span>
                              </div>
                            </div>
                            {!completed && item.remaining > 0 && (
                              <button
                                type="button"
                                onClick={() => {
                                  setMarkingKey(rowKey);
                                  logMutation.mutate({
                                    subscription_id: row.subscription_id,
                                    service_key: item.service_key,
                                    label: item.label,
                                  });
                                }}
                                disabled={marking}
                                aria-label={`Mark one ${item.label} done for ${row.client_name}`}
                                className="shrink-0 inline-flex items-center gap-1 rounded-md bg-[#0a0a14] text-white text-[10px] font-semibold px-2 py-1.5 hover:opacity-90 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-1"
                              >
                                {marking ? <Loader2 className="size-3 animate-spin" /> : <Check className="size-3" />}
                                Done
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })()}



            {/* Content Calendar */}
            <div className="bg-white rounded-xl border p-4" style={{ borderColor: "#eceff3" }}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-[#6c757d]">
                  Calendar <span className="text-[#adb5bd] font-medium normal-case ml-1">(7 days)</span>
                </h3>
                <Link
                  to={SMM_ROUTE_PATHS.dashboard}
                  search={{ view: "calendar" } as never}
                  className="text-xs font-semibold text-[#0d6efd] hover:underline whitespace-nowrap"
                >
                  Open calendar
                </Link>
              </div>
              {calendar.length === 0 ? (
                <p className="text-sm text-[#6b7280]">Nothing scheduled.</p>
              ) : (
                <ul className="space-y-2 text-xs">
                  {calendar.map((entry, i) => {
                    const { due, daysOffset, isOverdue } = entry;
                    const clientName = entry.kind === "scheduled" ? entry.clientName : entry.row.client_name;
                    const label = entry.kind === "scheduled" ? entry.label : entry.item.label;
                    const subId = entry.kind === "scheduled" ? entry.post.subscription_id : entry.row.subscription_id;
                    const svcKey = entry.kind === "scheduled" ? entry.post.service_key : entry.item.service_key;
                    const timeStr = entry.kind === "scheduled" && entry.post.scheduled_for
                      ? due.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
                      : null;
                    const tagBg = isOverdue ? "#fee2e2" : daysOffset === 0 ? "#fef3c7" : "#dcfce7";
                    const tagFg = isOverdue ? "#dc2626" : daysOffset === 0 ? "#b45309" : "#16a34a";
                    const tagText = isOverdue
                      ? "Overdue"
                      : daysOffset === 0
                        ? "Today"
                        : `In ${daysOffset} day${daysOffset === 1 ? "" : "s"}`;
                    return (
                      <li
                        key={`cal-${subId}-${svcKey}-${i}`}
                        className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-2 py-1.5 border-b border-[#f1f2f7] last:border-0"
                      >
                        <span className="text-[#6b7280] whitespace-nowrap">
                          {due.toLocaleString("en-US", { weekday: "short" })} {due.getDate()} {due.toLocaleString("en-US", { month: "short" })}
                          {timeStr && <span className="ml-1 tabular-nums">{timeStr}</span>}
                        </span>
                        <span className="font-semibold text-[#0a0a14] truncate">{clientName}</span>
                        <span className="text-[#6b7280] truncate">{label}</span>
                        <span
                          className="px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap"
                          style={{ background: tagBg, color: tagFg }}
                        >
                          {tagText}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Help tip */}
            <div className="bg-white rounded-xl border p-4" style={{ borderColor: "#eceff3" }}>
              <div className="flex items-start gap-3">
                <div className="size-8 rounded-md grid place-items-center bg-[#e7f1ff] shrink-0">
                  <Lightbulb className="size-4 text-[#0d6efd]" />
                </div>
                <div>
                  <p className="font-bold text-sm text-[#0a0a14]" style={{ fontFamily: HEADING_FONT }}>
                    How to update counts
                  </p>
                  <p className="text-xs text-[#6b7280] mt-1 leading-relaxed">
                    Click "+" to add a completed post. Hover over the counter and click "−" to remove.
                  </p>
                  <button className="text-xs font-semibold text-[#0d6efd] hover:underline mt-2">
                    Learn more about updating content
                  </button>
                </div>
              </div>
            </div>
          </aside>
        </div>

      {/* Bottom toast */}
      {toast && (
        <div
          className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:bottom-6 sm:left-1/2 sm:-translate-x-1/2 z-50 flex sm:inline-flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm shadow-2xl"
          style={{ background: "#0a0a14", color: "#fff" }}
        >
          <span className="size-5 rounded-full bg-[#22c55e] grid place-items-center shrink-0">
            <Check className="size-3 text-white" />
          </span>
          <span className="truncate flex-1">{toast.label}</span>
          {toast.token && (
            <button
              onClick={() => toast.token && undoMutation.mutate(toast.token)}
              className="font-semibold text-[#7dd3fc] hover:underline inline-flex items-center gap-1"
            >
              <Undo2 className="size-3.5" /> Undo
            </button>
          )}
          <button onClick={() => setToast(null)} className="text-white/60 hover:text-white">
            <X className="size-4" />
          </button>
          <span className="hidden md:inline text-white/50 text-xs ml-1">This will disappear in 5 seconds</span>
        </div>
      )}

      <BulkQuotaDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        selectedCount={selected.size}
        onSubmit={async (mode, quotas) => {
          const r = await bulkFn({
            data: { subscription_ids: Array.from(selected), mode, quotas },
          });
          setBulkOpen(false);
          setSelected(new Set());
          setToast({ label: `Updated ${r.updated} quota row(s)` });
          qc.invalidateQueries({ queryKey: ["smm", "dashboard"] });
        }}
      />

      <EditPlanDialog
        row={editRow}
        onClose={() => setEditRow(null)}
        onSubmit={async (payload) => {
          await updatePlanFn({ data: payload });
          setEditRow(null);
          setToast({ label: "Client plan updated" });
          qc.invalidateQueries({ queryKey: ["smm", "dashboard"] });
        }}
      />

      <DeliveryCalendarDialog
        open={calendarOpen !== null}
        onClose={() => setCalendarOpen(null)}
        deadlines={
          calendarOpen === "all" || calendarOpen === null
            ? allDeadlines
            : allDeadlines.filter((d) => d.row.subscription_id === calendarOpen)
        }
        title={
          calendarOpen && calendarOpen !== "all"
            ? allDeadlines.find((d) => d.row.subscription_id === calendarOpen)?.row.client_name ?? "Content Calendar"
            : "Content Calendar"
        }
      />

      {scheduleRow ? (
        <Suspense fallback={null}>
          <SmmScheduleDialog open={!!scheduleRow} onClose={() => setScheduleRow(null)} row={scheduleRow} />
        </Suspense>
      ) : null}

      <Dialog open={!!quotaPrompt} onOpenChange={(o) => !o && !quotaSaving && setQuotaPrompt(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Set Monthly Quota</DialogTitle>
          </DialogHeader>
          {quotaPrompt && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const n = parseInt(quotaValue, 10);
                if (!Number.isFinite(n) || n < 0) return;
                setQuotaSaving(true);
                try {
                  await updateQuotaFn({
                    data: {
                      subscription_id: quotaPrompt.row.subscription_id,
                      service_key: quotaPrompt.service_key,
                      label: quotaPrompt.label,
                      monthly_quota: n,
                    },
                  });
                  qc.invalidateQueries({ queryKey: ["smm", "dashboard"] });
                  setQuotaPrompt(null);
                } finally {
                  setQuotaSaving(false);
                }
              }}
              className="space-y-4"
            >
              <p className="text-sm text-muted-foreground">
                {quotaPrompt.label} · <span className="font-medium text-foreground">{quotaPrompt.row.client_name}</span>
              </p>
              <input
                type="number"
                min={0}
                autoFocus
                value={quotaValue}
                onChange={(e) => setQuotaValue(e.target.value)}
                className="w-full h-11 rounded-lg border border-[#e5e7eb] px-3 text-base focus:outline-none focus:ring-2 focus:ring-[#0d6efd]/30"
                placeholder="0"
              />
              <DialogFooter className="gap-2 sm:gap-2">
                <button
                  type="button"
                  onClick={() => setQuotaPrompt(null)}
                  disabled={quotaSaving}
                  className="h-9 px-3 rounded-lg text-sm font-semibold text-[#6b7280] hover:bg-zinc-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={quotaSaving}
                  className="h-9 px-4 rounded-lg text-sm font-semibold bg-[#0d6efd] text-white hover:bg-[#0b5ed7] disabled:opacity-60"
                >
                  {quotaSaving ? "Saving…" : "Save"}
                </button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function KpiTile({
  icon: Icon,
  tint,
  label,
  value,
  sub,
}: {
  icon: typeof Building2;
  tint: string;
  label: string;
  value: number;
  sub: string;
}) {
  return (
    <div className="rounded-xl border bg-white px-3 py-2.5" style={{ borderColor: "#eceff3" }}>
      <div className="flex items-center gap-2">
        <span className="size-7 rounded-md grid place-items-center shrink-0" style={{ background: `${tint}18`, color: tint }}>
          <Icon className="size-3.5" strokeWidth={1.85} />
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-[#6c757d] truncate">{label}</span>
      </div>
      <div className="mt-1.5 flex items-baseline gap-1.5">
        <span className="text-xl font-bold tabular-nums text-[#212529] leading-none">{value}</span>
        <span className="text-[10px] text-[#94a3b8] truncate">{sub}</span>
      </div>
    </div>
  );
}


function CellCounter({
  done,
  quota,
  status,
  label,
  active,
  onActivate,
  onCollapse,
  onCommitPlus,
  onCommitMinus,
}: {
  done: number;
  quota: number;
  status: string;
  label: string;
  active: boolean;
  onActivate: () => void;
  onCollapse: () => void;
  onCommitPlus: () => Promise<void>;
  onCommitMinus: () => Promise<void>;
}) {
  const subColor = STATUS_STYLES[status]?.chipFg ?? "#6b7280";
  const ref = useRef<HTMLDivElement>(null);
  const [delta, setDelta] = useState(0);
  const [saving, setSaving] = useState(false);

  const pendingDone = Math.max(0, Math.min(quota, done + delta));
  const remaining = Math.max(0, quota - pendingDone);
  const isDone = remaining === 0 && quota > 0;
  const hasPending = delta !== 0;

  useEffect(() => {
    if (!active) {
      setDelta(0);
      return;
    }
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (saving) return;
      if (ref.current && !ref.current.contains(e.target as Node)) {
        // Prevent accidental loss: keep open while user has unsaved changes
        if (!hasPending) onCollapse();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [active, hasPending, saving, onCollapse]);

  const inc = () => setDelta((d) => (done + d < quota ? d + 1 : d));
  const dec = () => setDelta((d) => (done + d > 0 ? d - 1 : d));

  const save = async () => {
    if (delta === 0) {
      onCollapse();
      return;
    }
    setSaving(true);
    try {
      const steps = Math.abs(delta);
      const fn = delta > 0 ? onCommitPlus : onCommitMinus;
      for (let i = 0; i < steps; i++) await fn();
      setDelta(0);
      onCollapse();
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    if (saving) return;
    setDelta(0);
    onCollapse();
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (!active) return;
    if (e.key === "ArrowUp" || e.key === "ArrowRight") { e.preventDefault(); inc(); }
    else if (e.key === "ArrowDown" || e.key === "ArrowLeft") { e.preventDefault(); dec(); }
    else if (e.key === "Enter") { e.preventDefault(); save(); }
    else if (e.key === "Escape") { e.preventDefault(); cancel(); }
  };

  return (
    <div
      ref={ref}
      role="group"
      aria-label={`${label} progress`}
      tabIndex={active ? -1 : 0}
      onKeyDown={onKey}
      onClick={() => !active && onActivate()}
      className={`flex flex-col items-center gap-1 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-1 ${!active ? "cursor-pointer" : ""}`}
    >
      <div className="inline-flex items-center justify-center gap-1 w-full">
        {active && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); dec(); }}
            disabled={saving || pendingDone === 0}
            className="size-8 sm:size-7 grid place-items-center rounded-full border border-[#e5e7eb] bg-white text-[#0a0a14] hover:bg-[#fee2e2] hover:border-[#fca5a5] hover:text-[#dc2626] disabled:opacity-30 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-1"
            aria-label={`Decrease ${label}`}
          >
            <Minus className="size-4 sm:size-3.5" />
          </button>
        )}
        <span
          className="font-bold tabular-nums text-base sm:text-sm min-w-[3ch] text-center text-[#0a0a14] leading-tight"
          aria-live={active ? "polite" : "off"}
          aria-atomic="true"
        >
          {active ? (
            <span className="block text-[11px] font-semibold text-slate-500 mb-0.5 sm:hidden">{label}:</span>
          ) : null}
          <span className="block">{active ? `${pendingDone} of` : `${pendingDone} / ${quota}`}</span>
          {active ? <span className="block text-xs text-slate-400 font-medium">{quota} / {delta > 0 ? `+${delta}` : delta < 0 ? delta : "1"}</span> : null}
          <span aria-hidden={active} className="hidden">{pendingDone} / {quota}</span>
        </span>
        {active && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); inc(); }}
            disabled={saving || pendingDone === quota}
            className="size-8 sm:size-7 grid place-items-center rounded-full border border-[#e5e7eb] bg-white text-[#0a0a14] hover:bg-[#dcfce7] hover:border-[#86efac] hover:text-[#16a34a] disabled:opacity-30 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-1"
            aria-label={`Increase ${label}`}
          >
            <Plus className="size-4 sm:size-3.5" />
          </button>
        )}
      </div>
      {active ? (
        <div className="grid grid-cols-2 gap-2 mt-2 w-full px-1" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={cancel}
            disabled={saving}
            className="inline-flex items-center justify-center gap-1 rounded-xl border border-[#e5e7eb] bg-white px-2 py-2.5 sm:py-2 text-xs font-semibold text-[#6b7280] hover:bg-[#f3f4f6] disabled:opacity-50 min-w-0 min-h-[44px] sm:min-h-[36px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-1"
            aria-label="Cancel edits"
          >
            <XIcon className="size-5 shrink-0 sm:size-4" />
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center justify-center gap-1 rounded-xl bg-[#0a0a14] px-2 py-2.5 sm:py-2 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60 min-w-0 min-h-[44px] sm:min-h-[36px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-1"
            aria-label="Save edits"
          >
            {saving ? <Loader2 className="size-5 shrink-0 animate-spin sm:size-4" /> : <Check className="size-5 shrink-0 sm:size-4" />}
          </button>
        </div>
      ) : (
        <div className="text-[11px] font-medium mt-0.5" style={{ color: subColor }}>
          {isDone ? (
            <span className="inline-flex items-center gap-1 text-[#16a34a]">
              <Check className="size-3" /> Done
            </span>
          ) : (
            `${remaining} left`
          )}
        </div>
      )}
    </div>
  );
}

function BulkQuotaDialog({
  open,
  onClose,
  selectedCount,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  selectedCount: number;
  onSubmit: (
    mode: "set" | "adjust",
    quotas: { stories?: number; feed_posts?: number; reels?: number },
  ) => Promise<void>;
}) {
  const [mode, setMode] = useState<"set" | "adjust">("set");
  const [stories, setStories] = useState("");
  const [feed, setFeed] = useState("");
  const [reels, setReels] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setStories("");
      setFeed("");
      setReels("");
      setMode("set");
    }
  }, [open]);

  const parse = (s: string) => {
    if (s.trim() === "") return undefined;
    const n = parseInt(s, 10);
    return Number.isFinite(n) ? n : undefined;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border border-[#eceef5]" style={{ fontFamily: BODY_FONT }}>
        <DialogHeader>
          <DialogTitle className="text-[#0a0a14]" style={{ fontFamily: HEADING_FONT }}>
            Bulk Quota Update — {selectedCount} client{selectedCount === 1 ? "" : "s"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode("set")}
              className="flex-1 px-3 py-2 rounded-lg text-sm font-semibold border transition-colors"
              style={
                mode === "set"
                  ? { background: "#0d6efd", borderColor: "#0d6efd", color: "#fff" }
                  : { background: "#fff", borderColor: "#e5e7eb", color: "#0a0a14" }
              }
            >
              Set to value
            </button>
            <button
              type="button"
              onClick={() => setMode("adjust")}
              className="flex-1 px-3 py-2 rounded-lg text-sm font-semibold border transition-colors"
              style={
                mode === "adjust"
                  ? { background: "#0d6efd", borderColor: "#0d6efd", color: "#fff" }
                  : { background: "#fff", borderColor: "#e5e7eb", color: "#0a0a14" }
              }
            >
              Adjust by ± value
            </button>
          </div>
          <p className="text-xs text-[#6b7280]">
            {mode === "set"
              ? "Sets each selected client's monthly quota to the value entered. Blank = unchanged."
              : "Adds (or subtracts) the value from each selected client's existing quota. Blank = skipped."}
          </p>
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "Stories", state: stories, set: setStories },
              { label: "Feed Posts", state: feed, set: setFeed },
              { label: "Reels", state: reels, set: setReels },
            ].map((f) => (
              <div key={f.label}>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-[#6b7280] mb-1">
                  {f.label}
                </label>
                <input
                  type="number"
                  value={f.state}
                  onChange={(e) => f.set(e.target.value)}
                  placeholder={mode === "set" ? "12" : "+2 / -1"}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-[#e5e7eb] bg-white text-[#0a0a14] placeholder:text-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#0d6efd]/30"
                />
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm rounded-lg border border-[#e5e7eb] bg-white text-[#0a0a14]"
          >
            Cancel
          </button>
          <button
            disabled={busy}
            onClick={async () => {
              const quotas = {
                stories: parse(stories),
                feed_posts: parse(feed),
                reels: parse(reels),
              };
              if (Object.values(quotas).every((v) => v === undefined)) return;
              setBusy(true);
              try {
                await onSubmit(mode, quotas);
              } finally {
                setBusy(false);
              }
            }}
            className="px-3 py-2 text-sm rounded-lg text-white font-semibold disabled:opacity-50 bg-[#0d6efd] hover:bg-[#0b5ed7]"
          >
            {busy ? "Applying..." : "Apply"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type EditPlanPayload = {
  subscription_id: string;
  package_type?: string;
  monthly_price?: number | null;
  ends_on?: string | null;
  quotas?: { stories?: number; feed_posts?: number; reels?: number };
};

const PACKAGE_OPTIONS = [
  "Starter Retainer",
  "Standard Retainer",
  "Premium Retainer",
  "Enterprise Retainer",
  "Custom",
];

function EditPlanDialog({
  row,
  onClose,
  onSubmit,
}: {
  row: SmmClientRow | null;
  onClose: () => void;
  onSubmit: (payload: EditPlanPayload) => Promise<void>;
}) {
  const open = !!row;
  const [packageType, setPackageType] = useState("");
  const [monthlyPrice, setMonthlyPrice] = useState("");
  const [endsOn, setEndsOn] = useState("");
  const [stories, setStories] = useState("");
  const [feed, setFeed] = useState("");
  const [reels, setReels] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!row) return;
    setPackageType(row.package_type ?? "");
    setMonthlyPrice(row.monthly_price != null ? String(row.monthly_price) : "");
    setEndsOn(row.ends_on ?? "");
    const find = (k: string) => row.items.find((i) => i.service_key === k)?.monthly_quota ?? 0;
    setStories(String(find("stories")));
    setFeed(String(find("feed_posts")));
    setReels(String(find("reels")));
  }, [row]);

  const parseN = (s: string) => {
    if (s.trim() === "") return undefined;
    const n = parseInt(s, 10);
    return Number.isFinite(n) && n >= 0 ? n : undefined;
  };
  const parseF = (s: string) => {
    if (s.trim() === "") return null;
    const n = parseFloat(s);
    return Number.isFinite(n) && n >= 0 ? n : null;
  };

  if (!row) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-white border border-[#eceef5] max-w-lg"
        style={{ fontFamily: BODY_FONT }}
      >
        <DialogHeader>
          <DialogTitle className="text-[#0a0a14]" style={{ fontFamily: HEADING_FONT }}>
            Edit Plan — {row.client_name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <section className="space-y-3">
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#6b7280]">
              Sold Line Items
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-[#0a0a14] mb-1">
                  Package
                </label>
                <select
                  value={packageType}
                  onChange={(e) => setPackageType(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-[#e5e7eb] bg-white text-[#0a0a14] focus:outline-none focus:ring-2 focus:ring-[#0d6efd]/30"
                >
                  {!PACKAGE_OPTIONS.includes(packageType) && packageType && (
                    <option value={packageType}>{packageType}</option>
                  )}
                  {PACKAGE_OPTIONS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#0a0a14] mb-1">
                  Monthly Price (€)
                </label>
                <input
                  type="number"
                  min={0}
                  step="50"
                  value={monthlyPrice}
                  onChange={(e) => setMonthlyPrice(e.target.value)}
                  placeholder="850"
                  className="w-full px-3 py-2 text-sm rounded-lg border border-[#e5e7eb] bg-white text-[#0a0a14] focus:outline-none focus:ring-2 focus:ring-[#0d6efd]/30"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#0a0a14] mb-1">
                  Contract Ends
                </label>
                <input
                  type="date"
                  value={endsOn ? endsOn.slice(0, 10) : ""}
                  onChange={(e) => setEndsOn(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-[#e5e7eb] bg-white text-[#0a0a14] focus:outline-none focus:ring-2 focus:ring-[#0d6efd]/30"
                />
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#6b7280]">
              Monthly Content Quotas
            </h4>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: "Stories", state: stories, set: setStories, key: "stories" as const },
                { label: "Feed Posts", state: feed, set: setFeed, key: "feed_posts" as const },
                { label: "Reels", state: reels, set: setReels, key: "reels" as const },
              ].map((f) => {
                const current = row.items.find((i) => i.service_key === f.key);
                return (
                  <div key={f.key}>
                    <label className="block text-xs font-semibold text-[#0a0a14] mb-1">
                      {f.label}
                    </label>
                    <input
                      type="number"
                      min={0}
                      value={f.state}
                      onChange={(e) => f.set(e.target.value)}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-[#e5e7eb] bg-white text-[#0a0a14] focus:outline-none focus:ring-2 focus:ring-[#0d6efd]/30"
                    />
                    {current && current.delivered > 0 && (
                      <p className="text-[10px] text-[#6b7280] mt-1">
                        {current.delivered} already delivered
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-[#6b7280]">
              Quotas reset every month. Set 0 to disable a content type for this client.
            </p>
          </section>
        </div>

        <DialogFooter>
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm rounded-lg border border-[#e5e7eb] bg-white text-[#0a0a14]"
          >
            Cancel
          </button>
          <button
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onSubmit({
                  subscription_id: row.subscription_id,
                  package_type: packageType || undefined,
                  monthly_price: parseF(monthlyPrice),
                  ends_on: endsOn || null,
                  quotas: {
                    stories: parseN(stories),
                    feed_posts: parseN(feed),
                    reels: parseN(reels),
                  },
                });
              } finally {
                setBusy(false);
              }
            }}
            className="px-3 py-2 text-sm rounded-lg text-white font-semibold disabled:opacity-50 bg-[#0d6efd] hover:bg-[#0b5ed7]"
          >
            {busy ? "Saving..." : "Save changes"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Delivery Calendar Dialog ───────────────────────────────────────────────
type DeadlineLite = {
  row: SmmClientRow;
  item: SmmProgressRow;
  due: Date;
  completed: boolean;
};

function DeliveryCalendarDialog({
  open,
  onClose,
  deadlines,
  title,
}: {
  open: boolean;
  onClose: () => void;
  deadlines: DeadlineLite[];
  title: string;
}) {
  const [monthOffset, setMonthOffset] = useState(0);

  const view = useMemo(() => {
    const base = new Date();
    base.setDate(1);
    base.setMonth(base.getMonth() + monthOffset);
    base.setHours(0, 0, 0, 0);
    const year = base.getFullYear();
    const month = base.getMonth();
    const first = new Date(year, month, 1);
    const startWeekday = (first.getDay() + 6) % 7; // Monday start
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells: (Date | null)[] = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return { year, month, cells, label: base.toLocaleString("en-US", { month: "long", year: "numeric" }) };
  }, [monthOffset]);

  const byDay = useMemo(() => {
    const m = new Map<string, DeadlineLite[]>();
    for (const d of deadlines) {
      const k = `${d.due.getFullYear()}-${d.due.getMonth()}-${d.due.getDate()}`;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(d);
    }
    return m;
  }, [deadlines]);

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayKey = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;
  const selectedList = selectedKey ? byDay.get(selectedKey) ?? [] : [];

  const totalPending = deadlines.filter((d) => !d.completed).length;
  const totalOverdue = deadlines.filter((d) => d.item.status === "overdue").length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="size-4 text-[#0d6efd]" />
            {title}
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-0.5">
            {totalPending} pending · {totalOverdue} overdue
          </p>
        </DialogHeader>

        <div className="flex items-center justify-between px-1 pb-2">
          <button
            onClick={() => setMonthOffset((m) => m - 1)}
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600"
            aria-label="Previous month"
          >
            <ChevronLeft className="size-4" />
          </button>
          <div className="text-sm font-bold text-slate-900">{view.label}</div>
          <button
            onClick={() => setMonthOffset((m) => m + 1)}
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600"
            aria-label="Next month"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 px-1 pb-1">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <div key={d} className="text-center py-1">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1 px-1">
          {view.cells.map((cell, idx) => {
            if (!cell) return <div key={idx} className="aspect-square" />;
            const key = `${cell.getFullYear()}-${cell.getMonth()}-${cell.getDate()}`;
            const items = byDay.get(key) ?? [];
            const hasOverdue = items.some((i) => i.item.status === "overdue" && !i.completed);
            const hasPending = items.some((i) => !i.completed);
            const isToday = key === todayKey;
            const isSelected = key === selectedKey;
            return (
              <button
                key={idx}
                onClick={() => setSelectedKey(items.length ? key : null)}
                className={`aspect-square rounded-lg border text-xs flex flex-col items-center justify-center gap-0.5 transition-colors ${
                  isSelected
                    ? "border-[#0d6efd] bg-[#eef0ff]"
                    : isToday
                    ? "border-slate-900 bg-slate-50"
                    : "border-slate-200 bg-white hover:bg-slate-50"
                } ${items.length === 0 ? "opacity-70" : ""}`}
              >
                <span className={`font-semibold ${isToday ? "text-slate-900" : "text-slate-700"}`}>
                  {cell.getDate()}
                </span>
                {items.length > 0 && (
                  <span className="flex items-center gap-0.5">
                    <span
                      className={`size-1.5 rounded-full ${
                        hasOverdue ? "bg-red-500" : hasPending ? "bg-amber-500" : "bg-emerald-500"
                      }`}
                    />
                    <span className="text-[9px] text-slate-500">{items.length}</span>
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-4 border-t border-slate-200 pt-3">
          {selectedKey && selectedList.length > 0 ? (
            <>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                {new Date(selectedList[0].due).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}
              </div>
              <ul className="space-y-1.5 max-h-56 overflow-y-auto">
                {selectedList.map((d, i) => (
                  <li key={i} className="flex items-center justify-between gap-2 text-sm p-2 rounded-lg bg-slate-50">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{d.row.client_name}</div>
                      <div className="text-xs text-slate-500 truncate">{d.item.label} · {d.item.remaining} remaining</div>
                    </div>
                    <span
                      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
                        d.item.status === "overdue"
                          ? "bg-red-100 text-red-700"
                          : d.completed
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {d.item.status === "overdue" ? "Overdue" : d.completed ? "Done" : "Pending"}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-xs text-slate-500 text-center py-6">
              {deadlines.length === 0 ? "No deliverables scheduled." : "Click a highlighted day to see deliverables."}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
